import { P2GBox, P2GCheckBoxLegacy, P2GLink, P2GText } from '@parcel2go/core-ui';
import { PanelButtons } from '../panels/PanelButtons';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { RuleApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { useEffect, useState } from 'react';
import { onlyUnique } from '~/lib/utils/array';
import { TariffSuggestions } from '../../customs/TariffSuggestions';
import { TariffCode } from '../../customs/TariffCode';
import GetTariffSystemFromItem from '~/apps/smartsend/lib/helpers/GetTariffSystemFromItem';
import { BulkTariffCodes } from './BulkTariffCodes';
import SidePanelSection from '~/components/sidepanel-section';
import { Actions } from '../rules/Actions';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { Conditions } from '../rules/Conditions';
import { ActionType } from '../rules/ActionType';
import { uid } from '~/apps/smartsend/lib/helpers/uid';
import { Comparisons } from '../rules/Comparisons';
import { TruncateString } from '~/apps/smartsend/lib/helpers/StringHelper';
import { Operators } from '../rules/Operators';
import { RuleNames } from '../rules/RuleGenerator';

interface props {
  affected: number;
  items: () => SmartSendItemApiModel[];
  confirm?: (codes: BulkTariffCodes) => void;
  cancel?: () => void;
}

export default function ApplyTariffCodes(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { getRules, deleteRule, createRule, updateRule } = useSmartSendApi();
  const [codes, setCodes] = useState<BulkTariffCodes>({});
  const [rules, setRules] = useState<RuleApiModel[]>([]);
  const [storeCodes, setStoreCodes] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const load = async () => {
      const r = await getRules('SmartSend');
      setRules(r.success ? r.result.Rules : []);
      if (r.success) {
        const existingRules = r.result.Rules.filter(
          (x) =>
            x.Conditions.length &&
            x.Conditions.every((y) => y.Field === Conditions.Item_Name) &&
            x.Actions.length &&
            x.Actions.every((y) => y.Field === Actions.Item_TariffCode)
        )
          .flatMap((x) => x.Conditions)
          .flatMap((y) => y.Values)
          .filter(onlyUnique);
        setStoreCodes(Object.fromEntries(existingRules.map((x) => [x, true])));
      }
    };
    load();
  }, []);

  useEffect(() => {
    const masterItems = props.items();
    const tariffSystems = masterItems.map(GetTariffSystemFromItem).filter(onlyUnique);
    const output: BulkTariffCodes = {} as BulkTariffCodes;
    tariffSystems.forEach((system) => {
      const items = masterItems
        .filter((x) => GetTariffSystemFromItem(x) === system)
        .flatMap((x) => x.MarketplaceOrderLines);
      const descriptions = items.map((x) => x.Product.Description).filter(onlyUnique);
      output[system] = Object.fromEntries(descriptions.map((x) => [x, '']));
    });
    setCodes(output);
  }, []);

  const onSave = async () => {
    if (props.confirm) {
      const codesWithValues = Object.fromEntries(
        Object.entries(codes).filter(([_, value]) => value && Object.keys(value).length > 0)
      );

      for (const tariffSystem in codesWithValues) {
        for (const desc in codesWithValues[tariffSystem]) {
          const code = codesWithValues[tariffSystem][desc];

          const existingRule = rules.find(
            (r) =>
              r.Conditions.length &&
              r.Conditions.every((c) => c.Field === Conditions.Item_Name && c.Values.includes(desc)) &&
              r.Actions.length &&
              r.Actions.every((a) => a.Field === Actions.Item_TariffCode && a.Value === code)
          );

          if (storeCodes[desc]) {
            // Create or upddate if existing rule
            if (existingRule) {
              existingRule.Actions.forEach((a) => {
                if (a.Field === Actions.Item_TariffCode) {
                  a.Value = code;
                }
              });
              await updateRule(existingRule);
            } else {
              await createRule({
                Conditions: [
                  {
                    Comparison: Comparisons.IsOneOf,
                    Field: Conditions.Item_Name,
                    Values: [desc],
                    Id: uid()
                  }
                ],
                Actions: [
                  {
                    Field: Conditions.Item_TariffCode,
                    Type: ActionType.Replace,
                    Value: code,
                    Id: uid()
                  }
                ],
                Name: `Apply Tariff Code to "${TruncateString(desc, 50)}"`,
                Description: `Automatically applied tariff code "${code}" for items matching description "${TruncateString(desc, 100)}"`,
                Active: true,
                RuleType: 'SmartSend',
                ConditionType: Operators.Or,
                Priority: rules.map((x) => x.Priority).reduce((a, b) => Math.max(a, b), 0) + 1,
                Tags: [RuleNames.InlineTag]
              });
            }
          } else {
            // Delete if has existing rule
            if (existingRule) {
              await deleteRule(existingRule.Id, 'SmartSend');
            }
          }
        }
      }

      props.confirm(codesWithValues);
    }
  };

  return (
    <>
      <P2GBox display="flex" flexDirection="column" gap={2}>
        {Object.entries(codes).map(([key, value]) => (
          <SidePanelSection key={key} title={`${key} Tariff Code Suggestions`}>
            <P2GBox p={2} display="flex" flexDirection="column" gap={2}>
              {Object.entries(value).map(([nKey, nValue]) => (
                <P2GBox key={nKey} display="flex" flexDirection="column" gap={2}>
                  <P2GBox>
                    <P2GText>
                      <strong>{nKey}</strong>
                    </P2GText>
                  </P2GBox>
                  <P2GBox>
                    <TariffCode code={nValue} selectable={false}>
                      {nValue && (
                        <P2GLink
                          variant="body2"
                          onClick={() => {
                            setCodes((prev) => ({ ...prev, [key]: { ...prev[key], [nKey]: '' } }));
                          }}
                        >
                          Clear
                        </P2GLink>
                      )}
                    </TariffCode>
                  </P2GBox>
                  <P2GBox>
                    {!nValue ? (
                      <TariffSuggestions
                        title="Suggestion:"
                        canSelect={true}
                        limit={1}
                        selectCode={(v) => setCodes((prev) => ({ ...prev, [key]: { ...prev[key], [nKey]: v } }))}
                        tariffSystem={key}
                        itemDescription={nKey}
                      />
                    ) : (
                      <P2GCheckBoxLegacy
                        id={`apply-tariff-${key}-${nKey}`}
                        label={t('SMARTSEND_APPLY_TARIFF_CODE', { defaultValue: 'Always apply code to this item' })}
                        onChange={(v) => setStoreCodes((prev) => ({ ...prev, [nKey]: v }))}
                        disabled={!nValue}
                        checked={storeCodes[nKey] ?? false}
                      />
                    )}
                  </P2GBox>
                </P2GBox>
              ))}
            </P2GBox>
          </SidePanelSection>
        ))}
        <P2GBox mt={2} />
        <PanelButtons
          onSave={props.confirm && onSave}
          onCancel={props.cancel}
          cancelText={t('CANCEL_BUTTON', { defaultValue: 'Cancel' })}
        />
      </P2GBox>
    </>
  );
}
